// Sprint 4 — Dashboard KPIs y actividad reciente
import { css } from "styled-system/css";
import { formatInTimeZone } from "date-fns-tz";
import { motion, useReducedMotion } from "motion/react";
import type { AppointmentStatus } from "@prisma/client";
import { labelAppointmentStatus } from "@/lib/dashboard-labels";
import { WhatsAppChatLink } from "@/components/dashboard/whatsapp-chat-link";

export type RecentActivityRow = {
  id: string;
  startTime: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string | null;
  status: AppointmentStatus;
  service: { name: string };
  professional?: { name: string };
};

type Props = {
  rows: RecentActivityRow[];
  timezone: string;
  /** true = columna "Agenda" con nombre del profesional; false = columna "Servicio". */
  agendaIsProfessional: boolean;
};

function statusTone(status: AppointmentStatus) {
  if (status === "COMPLETED") return { bg: "#ecfdf5", color: "#166534", border: "#bbf7d0" };
  if (status === "NO_SHOW") return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
  if (status === "CANCELLED") return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  if (status === "CONFIRMED") return { bg: "#eef2ff", color: "#3730a3", border: "#c7d2fe" };
  return { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" };
}

export function DashboardRecentActivityTable({ rows, timezone, agendaIsProfessional }: Props) {
  const shouldReduceMotion = useReducedMotion();
  const wrap = css({
    w: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  });

  const table = css({
    w: "100%",
    minW: "720px",
    borderCollapse: "collapse",
    fontSize: "sm",
  });

  const th = css({
    textAlign: "left",
    fontSize: "xs",
    fontWeight: "semibold",
    color: "#64748b",
    pb: "3",
    borderBottomWidth: "1px",
    borderColor: "#e2e8f0",
    pr: "4",
  });

  const td = css({
    py: "3",
    pr: "4",
    borderBottomWidth: "1px",
    borderColor: "#f1f5f9",
    color: "#334155",
    verticalAlign: "middle",
  });

  return (
    <div className={wrap}>
      <table className={table}>
        <thead>
          <tr>
            <th className={th}>{agendaIsProfessional ? "Agenda" : "Servicio"}</th>
            <th className={th}>Cliente</th>
            <th className={th}>Email</th>
            <th className={th}>Fecha y hora</th>
            <th className={th}>WhatsApp</th>
            <th className={th}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => {
            const tone = statusTone(a.status);
            const agendaCell = agendaIsProfessional
              ? a.professional?.name?.trim() || "—"
              : a.service?.name?.trim() || "—";
            const when = `${formatInTimeZone(new Date(a.startTime), timezone, "dd/MM/yyyy")} · ${formatInTimeZone(new Date(a.startTime), timezone, "HH:mm")}`;
            return (
              <motion.tr
                key={a.id}
                initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
              >
                <td className={td}>{agendaCell}</td>
                <td className={td}>{a.clientName}</td>
                <td className={td}>{a.clientEmail}</td>
                <td className={td}>{when}</td>
                <td className={td}>
                  <WhatsAppChatLink phone={a.clientPhone} variant="compact" />
                </td>
                <td className={td}>
                  <span
                    className={css({
                      fontSize: "xs",
                      fontWeight: "semibold",
                      padding: "6px 10px",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor: tone.border,
                      background: tone.bg,
                      color: tone.color,
                      borderRadius: "999px",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {labelAppointmentStatus(a.status)}
                  </span>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
